import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { Settings, Menu, X } from "lucide-react";
import SettingsPanel from "@/components/landing/SettingsPanel";
import { FLAGS } from "@/lib/featureFlags";

interface NavbarProps {
  onCTA?: () => void;
  useAnchors?: boolean;
  activePage?: string;
}

export default function Navbar({ onCTA, useAnchors = false, activePage }: NavbarProps) {
  const { t, i18n } = useTranslation();
  const hue = 235;
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeSection, setActiveSection] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 60);
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? (y / max) * 100 : 0);

      // Track active section
      const sections = [
        "services",
        ...(FLAGS.showWorkSection ? ["work"] : []),
        "pricing",
        "about",
        "contact",
      ];
      let current = "";
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120) current = id;
        }
      }
      setActiveSection(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navColor = "var(--color-nav-light)";

  const logoFilter = `brightness(0) saturate(100%) invert(85%) sepia(10%) saturate(400%) hue-rotate(${hue}deg) brightness(110%)`;

  // linkDefs drives BOTH the desktop horizontal nav and the mobile
  // slide-down menu, so filtering here hides the Work link in both.
  const linkDefs = [
    { label: t("dyna.nav.home"),     href: "#top",      page: "home" },
    { label: t("dyna.nav.services"), href: "#services", page: "services" },
    ...(FLAGS.showWorkSection
      ? [{ label: t("dyna.nav.work"), href: "#work", page: "work" }]
      : []),
    { label: t("dyna.nav.pricing"),  href: "#pricing",  page: "packages" },
    { label: t("dyna.nav.about"),    href: "#about",    page: "about" },
    { label: t("dyna.nav.blog"),     href: "/blog",     page: "blog" },
  ];

  const isActivePage = (page: string) => {
    if (activePage) return activePage === page;
    if (!useAnchors) {
      if (page === "home") return location.pathname === "/";
      return location.pathname === `/${page}`;
    }
    return activeSection === page;
  };

  return (
    <>
      <header style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 50,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        minHeight: 72,
        padding: "0 clamp(24px, 5vw, 80px)",
        gap: 16,
        background: scrolled
          ? "color-mix(in srgb, var(--dyna-bg) 55%, transparent)"
          : "transparent",
        borderBottom: scrolled ? "1px solid var(--dyna-border)" : "1px solid transparent",
        backdropFilter: scrolled ? "blur(16px) saturate(140%)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(16px) saturate(140%)" : "none",
        boxShadow: scrolled ? "0 1px 0 color-mix(in srgb, var(--dyna-border) 40%, transparent), 0 8px 24px rgba(0,0,0,0.08)" : "none",
        transition: "background 300ms ease-out, border-color 300ms ease-out, backdrop-filter 300ms ease-out, box-shadow 300ms ease-out",
      }}>
        <div className="dyna-progress" style={{ width: `${progress}%`, opacity: progress > 0 ? 1 : 0 }} />

        {/* Logo */}
        {useAnchors ? (
          <a href="#top" style={{ display: "flex", alignItems: "center", textDecoration: "none", flexShrink: 0 }}>
            <img src="/wordmark.webp" alt="DYNA" width="298" height="104" style={{ height: "clamp(22px, 3vw, 28px)", width: "auto", filter: logoFilter, transition: "filter 300ms" }} />
          </a>
        ) : (
          <Link to="/" style={{ display: "flex", alignItems: "center", textDecoration: "none", flexShrink: 0 }}>
            <img src="/wordmark.webp" alt="DYNA" width="298" height="104" style={{ height: "clamp(22px, 3vw, 28px)", width: "auto", filter: logoFilter, transition: "filter 300ms" }} />
          </Link>
        )}

        {/* Center section — desktop horizontal nav. Hidden on mobile.
            Sits between the logo and right cluster as the second of three
            flex children of the outer header so justify-between spreads
            logo / nav / right across the full width. */}
        <nav className="hidden md:flex items-center gap-1" style={{ flexShrink: 0 }}>
          {linkDefs.map((l) => {
            const isActive = isActivePage(l.page);
            const linkStyle = {
              fontFamily: "Geist, system-ui, sans-serif",
              fontWeight: isActive ? 700 : 600,
              fontSize: 16,
              color: navColor,
              textDecoration: "none",
              letterSpacing: "0.04em",
              transition: "color 200ms, font-weight 200ms",
              position: "relative" as const,
              padding: "0 12px",
            };

            if (useAnchors) {
              return (
                <a key={l.page} href={l.href} className="dyna-nav-link" style={linkStyle}>
                  {l.label}
                </a>
              );
            }
            return (
              <Link
                key={l.page}
                to={l.page === "home" ? "/" : l.page === "packages" ? "/packages" : `/${l.page}`}
                className="dyna-nav-link"
                style={linkStyle}
                onClick={
                  l.page === "home" && location.pathname === "/"
                    ? (e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }
                    : undefined
                }
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        {/* Right cluster — CTA (desktop only) + always-visible icons.
            Hamburger appears only below md. */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <button
            className="dyna-btn dyna-btn--primary hidden md:inline-flex"
            onClick={onCTA}
            style={{ fontSize: 12, padding: "9px 18px", marginInlineEnd: 6 }}
          >
            {t("dyna.hero.cta_primary")} <span className="dyna-arrow">→</span>
          </button>
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 36, height: 36, borderRadius: "50%",
              background: "var(--ctrl-bg)", border: "1px solid var(--ctrl-border)",
              color: "var(--ctrl-fg)", cursor: "pointer",
              transition: "background 200ms, border-color 200ms",
            }}
            aria-label="Settings"
          >
            <Settings size={16} />
          </button>
          <button
            type="button"
            onClick={() => {
              const next = i18n.language === "he" ? "en" : "he";
              i18n.changeLanguage(next);
            }}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 36, height: 36, borderRadius: "50%",
              background: "var(--ctrl-bg)", border: "1px solid var(--ctrl-border)",
              color: "var(--ctrl-fg)", cursor: "pointer",
              fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
              letterSpacing: "0.06em", fontWeight: 600,
              transition: "background 200ms, border-color 200ms",
            }}
            aria-label="Toggle language"
          >
            {i18n.language === "he" ? "EN" : "עב"}
          </button>
          <button
            type="button"
            onClick={() => setMobileMenuOpen(o => !o)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            className="flex md:hidden items-center justify-center"
            style={{
              width: 36, height: 36, borderRadius: "50%",
              background: "var(--ctrl-bg)", border: "1px solid var(--ctrl-border)",
              color: "var(--ctrl-fg)", cursor: "pointer",
              transition: "background 200ms",
            }}
          >
            {mobileMenuOpen ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </header>

      {/* Mobile nav menu */}
      <div className="dyna-mobile-nav" data-open={String(mobileMenuOpen)}>
        {linkDefs.map((l) => {
          if (useAnchors) {
            return (
              <a key={l.page} href={l.href} onClick={() => setMobileMenuOpen(false)}>
                {l.label}
              </a>
            );
          }
          return (
            <Link
              key={l.page}
              to={l.page === "home" ? "/" : l.page === "packages" ? "/packages" : `/${l.page}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              {l.label}
            </Link>
          );
        })}
        <div className="dyna-mobile-nav__cta">
          <button className="dyna-btn dyna-btn--primary" onClick={() => { setMobileMenuOpen(false); onCTA?.(); }} style={{ width: "100%", justifyContent: "center" }}>
            {t("dyna.hero.cta_primary")} <span className="dyna-arrow">→</span>
          </button>
        </div>
      </div>

      <SettingsPanel open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  );
}
